import {
  DESCRIPCION_ROL,
  DESCRIPCION_ROL_EN,
  ETIQUETA_ROL,
  ETIQUETA_ROL_EN,
  ROLES,
  type Rol,
  type Usuario,
  type UsuarioConContrasenaTemporal,
} from '@cnc/contracts';
import {useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {useCrearUsuario, useEditarUsuario, useRestablecerContrasena, useUsuarios} from '../api/sesion';
import {AccesosDeUsuario} from '../componentes/accesos-de-usuario';
import {resumenDeAccesos} from '../componentes/areas';
import {AYUDA_USUARIOS as A} from '../componentes/ayuda-general';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {useUsuario} from '../componentes/sesion-contexto';
import {et, tr} from '../i18n/idioma';
import {fechaHora, textoOGuion} from '../utilidades/formato';

const clase = 'mt-1 w-full rounded-md border border-linea px-3 py-2 text-sm text-tinta';
const boton = 'rounded-md bg-verde px-4 py-2 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50';
const botonSuave = 'rounded-md border border-linea bg-white px-2.5 py-1 text-xs hover:bg-papel disabled:opacity-40';

// Administracion de usuarios: crear (con contrasena temporal que se muestra
// una sola vez), cambiar rol, desactivar o reactivar y restablecer la contrasena.
export function PaginaUsuarios() {
  const yo = useUsuario();
  const usuarios = useUsuarios();
  const crear = useCrearUsuario();
  const editar = useEditarUsuario();
  const restablecer = useRestablecerContrasena();
  const [nombre, setNombre] = useState('');
  const [correo, setCorreo] = useState('');
  const [rol, setRol] = useState<Rol>('viewer');
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [temporal, setTemporal] = useState<UsuarioConContrasenaTemporal | null>(null);
  // Persona cuyas secciones y pestanas se estan eligiendo (decision 0062).
  const [deAccesos, setDeAccesos] = useState<number | null>(null);
  const conAccesos = usuarios.data?.usuarios.find(u => u.id === deAccesos) ?? null;
  const etiquetaRol = et(ETIQUETA_ROL, ETIQUETA_ROL_EN);

  const conError = (error: unknown, porDefecto: string) =>
    setMensaje(error instanceof ErrorDeApi ? [error.message, ...(error.detalles ?? [])].join(' ') : porDefecto);

  const crearUsuario = async () => {
    setMensaje(null);
    try {
      const creado = await crear.mutateAsync({nombre: nombre.trim(), correo: correo.trim().toLowerCase(), rol});
      setTemporal(creado);
      setNombre('');
      setCorreo('');
      setRol('viewer');
    } catch (error) {
      conError(error, tr('No se pudo crear el usuario.', 'Could not create the user.'));
    }
  };

  const cambiar = async (usuario: Usuario, cambios: {rol?: Rol; activo?: boolean}) => {
    setMensaje(null);
    try {
      await editar.mutateAsync({id: usuario.id, cambios});
    } catch (error) {
      conError(error, tr('No se pudo guardar el cambio.', 'Could not save the change.'));
    }
  };

  const restablecerDe = async (usuario: Usuario) => {
    setMensaje(null);
    const quitarDosPasos =
      usuario.dosPasosActivo &&
      window.confirm(
        tr(
          `${usuario.nombre} tiene la verificación en dos pasos activa. ¿Quitarla también (perdió el teléfono)?`,
          `${usuario.nombre} has two-step verification turned on. Remove it too (lost phone)?`,
        ),
      );
    if (
      !window.confirm(
        tr(
          `Se generará una contraseña temporal para ${usuario.nombre} y se cerrarán sus sesiones. ¿Continuar?`,
          `A temporary password will be generated for ${usuario.nombre} and their sessions will be closed. Continue?`,
        ),
      )
    )
      return;
    try {
      setTemporal(await restablecer.mutateAsync({id: usuario.id, opciones: {quitarDosPasos}}));
    } catch (error) {
      conError(error, tr('No se pudo restablecer la contraseña.', 'Could not reset the password.'));
    }
  };

  return (
    <section className="max-w-6xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Usuarios', 'Users')}</h1>
        <p className="text-sm text-apagado">
          {tr(
            'Quién puede entrar y con qué rol. Cada persona nueva recibe una contraseña temporal de un solo uso y la cambia al entrar.',
            'Who can sign in and with which role. Each new person gets a single-use temporary password and changes it on first sign-in.',
          )}
        </p>
      </div>

      {temporal && (
        <div className="rounded-lg border border-verde/50 bg-verde-claro p-4 text-sm">
          <div className="font-medium text-verde-oscuro">
            {tr(
              `Contraseña temporal de ${temporal.usuario.nombre} (${temporal.usuario.correo})`,
              `Temporary password for ${temporal.usuario.nombre} (${temporal.usuario.correo})`,
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <code className="rounded bg-white px-3 py-1.5 text-2xl tracking-[0.3em]">
              {temporal.contrasenaTemporal}
            </code>
            <span className="text-apagado">
              {tr(
                'Se muestra una sola vez: entrégasela por un canal seguro. Al entrar deberá cambiarla.',
                'It is shown only once: hand it over through a secure channel. They must change it when signing in.',
              )}
            </span>
            <button
              type="button"
              onClick={() => setTemporal(null)}
              className="ml-auto text-xs text-apagado hover:text-tinta"
            >
              ✕ {tr('Ocultar', 'Hide')}
            </button>
          </div>
        </div>
      )}

      <form
        className="rounded-lg border border-linea bg-white p-4"
        onSubmit={e => {
          e.preventDefault();
          void crearUsuario();
        }}
      >
        <h2 className="font-medium">{tr('Nuevo usuario', 'New user')}</h2>
        <div className="mt-2 grid gap-3 md:grid-cols-[1fr_1fr_auto_auto] md:items-end">
          <label className="block text-xs text-apagado">
            {tr('Nombre', 'Name')}
            <NotaDeAyuda texto={A.nombre} dentroDeEtiqueta />
            <input value={nombre} onChange={e => setNombre(e.target.value)} required minLength={2} className={clase} />
          </label>
          <label className="block text-xs text-apagado">
            {tr('Correo', 'Email')}
            <NotaDeAyuda texto={A.correo} dentroDeEtiqueta />
            <input type="email" value={correo} onChange={e => setCorreo(e.target.value)} required className={clase} />
          </label>
          <label className="block text-xs text-apagado">
            {tr('Rol', 'Role')}
            <NotaDeAyuda texto={A.rol} dentroDeEtiqueta />
            <select
              value={rol}
              onChange={e => setRol(e.target.value as Rol)}
              aria-label={tr('Rol del nuevo usuario', 'Role of the new user')}
              className={clase}
            >
              {ROLES.map(r => (
                <option key={r} value={r}>
                  {etiquetaRol[r]}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" disabled={crear.isPending} className={boton}>
            {crear.isPending ? tr('Creando…', 'Creating…') : tr('Crear', 'Create')}
          </button>
        </div>
        <p className="mt-2 text-xs text-apagado">{et(DESCRIPCION_ROL, DESCRIPCION_ROL_EN)[rol]}</p>
        {mensaje && <p className="mt-2 text-sm text-alerta">{mensaje}</p>}
      </form>

      <div className="overflow-auto rounded-lg border border-linea bg-white">
        <table className="w-full text-sm">
          <thead className="bg-papel">
            <tr className="text-left text-xs uppercase tracking-wide text-apagado">
              <th className="px-3 py-2 font-semibold">
                {tr('Nombre', 'Name')}
                <NotaDeAyuda texto={A.nombre} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Correo', 'Email')}
                <NotaDeAyuda texto={A.correo} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Rol', 'Role')}
                <NotaDeAyuda texto={A.rolTabla} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Secciones', 'Sections')}
                <NotaDeAyuda texto={A.accesos} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Estado', 'Status')}
                <NotaDeAyuda texto={A.estado} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Dos pasos', 'Two-step')}
                <NotaDeAyuda texto={A.dosPasos} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Último ingreso', 'Last sign-in')}
                <NotaDeAyuda texto={A.ultimoIngreso} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Sesiones', 'Sessions')}
                <NotaDeAyuda texto={A.sesiones} />
              </th>
              <th className="px-3 py-2 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {usuarios.data?.usuarios.map(u => {
              const soyYo = u.id === yo.id;
              return (
                <tr key={u.id} className={`border-t border-linea/70 ${u.activo ? '' : 'text-apagado'}`}>
                  <td className="px-3 py-2 font-medium">
                    {u.nombre}
                    {soyYo && <span className="ml-1 text-xs text-apagado">{tr('(tú)', '(you)')}</span>}
                  </td>
                  <td className="px-3 py-2">{u.correo}</td>
                  <td className="px-3 py-2">
                    <select
                      value={u.rol}
                      disabled={soyYo || editar.isPending}
                      onChange={e => void cambiar(u, {rol: e.target.value as Rol})}
                      aria-label={tr(`Rol de ${u.nombre}`, `Role of ${u.nombre}`)}
                      className="rounded-md border border-linea bg-white px-2 py-1 text-sm text-tinta disabled:opacity-60"
                    >
                      {ROLES.map(r => (
                        <option key={r} value={r}>
                          {etiquetaRol[r]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="text-xs">{resumenDeAccesos(u)}</span>
                    {u.rol !== 'admin' && (
                      <button
                        type="button"
                        onClick={() => setDeAccesos(deAccesos === u.id ? null : u.id)}
                        className={`ml-2 ${botonSuave}`}
                        aria-label={tr(`Elegir secciones de ${u.nombre}`, `Choose sections of ${u.nombre}`)}
                      >
                        {tr('Elegir', 'Choose')}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {u.activo ? tr('Activo', 'Active') : tr('Desactivado', 'Deactivated')}
                    {u.bloqueadoHasta && new Date(u.bloqueadoHasta) > new Date() && (
                      <span className="ml-1 text-xs text-alerta">
                        {tr('· bloqueado por intentos fallidos', '· locked after failed attempts')}
                      </span>
                    )}
                    {u.debeCambiarContrasena && (
                      <span className="ml-1 text-xs text-apagado">
                        {tr('· debe cambiar contraseña', '· must change password')}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">{u.dosPasosActivo ? tr('Activa', 'On') : '—'}</td>
                  <td className="px-3 py-2">{u.ultimoIngreso ? fechaHora(u.ultimoIngreso) : tr('Nunca', 'Never')}</td>
                  <td className="px-3 py-2 tabular-nums">{u.sesionesAbiertas}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => void restablecerDe(u)}
                      disabled={restablecer.isPending}
                      className={botonSuave}
                    >
                      {tr('Restablecer contraseña', 'Reset password')}
                    </button>{' '}
                    <button
                      type="button"
                      onClick={() => void cambiar(u, {activo: !u.activo})}
                      disabled={soyYo || editar.isPending}
                      className={botonSuave}
                    >
                      {u.activo ? tr('Desactivar', 'Deactivate') : tr('Reactivar', 'Reactivate')}
                    </button>
                  </td>
                </tr>
              );
            })}
            {usuarios.data && usuarios.data.usuarios.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-apagado">
                  {textoOGuion(null)} {tr('No hay usuarios.', 'There are no users.')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {conAccesos && conAccesos.rol !== 'admin' && (
        <AccesosDeUsuario key={conAccesos.id} usuario={conAccesos} onCerrar={() => setDeAccesos(null)} />
      )}
      <p className="text-xs text-apagado">
        {tr(
          'Cambiar el rol o desactivar a alguien cierra sus sesiones al instante. Siempre debe quedar al menos un administrador activo; nadie puede cambiar su propio rol ni desactivarse.',
          'Changing someone’s role or deactivating them closes their sessions immediately. At least one active administrator must always remain; nobody can change their own role or deactivate themselves.',
        )}
      </p>
    </section>
  );
}
